import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Briefcase, Sparkle, HandsPraying, Shield, Bell } from "@phosphor-icons/react";
import { PageShell } from "../components/Layout";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";
import PaywallGate from "../components/PaywallGate";
import SEO from "../components/SEO";
import useBirthDetailsPrefill from "../hooks/useBirthDetailsPrefill";
import api from "../lib/api";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));
const inputCls = "mt-1 block w-full min-w-0 rounded-xl border-2 border-[#E8A628]/50 focus:border-[#E8A628] outline-none bg-white px-3 py-2.5 text-[15px]";
const fmt = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
const dms = (d) => `${Math.floor(d)}°${String(Math.round((d % 1) * 60)).padStart(2, "0")}′`;

const PeriodCard = ({ d }) => (
  <section className="rounded-2xl bg-white border border-amber-200/70 p-5 sm:p-7" data-testid="d10r-period-card">
    <div className="label-eyebrow text-muted">Your running periods</div>
    <div className="grid sm:grid-cols-3 gap-3 mt-4 text-sm">
      {[["Mahadasha", d.mahadasha, "d10r-md", d.mahadasha.lord],
        ["Current Antardasha", d.antardasha, "d10r-ad", `${d.mahadasha.lord}–${d.antardasha.lord}`],
        ...(d.next_antardasha ? [["Next Antardasha", d.next_antardasha, "d10r-next", `${d.next_antardasha.mahadasha_lord}–${d.next_antardasha.lord}`]] : []),
      ].map(([label, p, id, name]) => (
        <div key={id} className={`rounded-xl border p-4 ${label === "Current Antardasha" ? "border-[#E8A628]" : "border-amber-200/70"}`}>
          <div className="text-xs text-muted">{label}</div>
          <div className="font-serif-display text-2xl text-[#3F2D1B]" data-testid={`${id}-lord`}>{name}</div>
          <div className="text-xs text-muted mt-0.5" data-testid={`${id}-dates`}>{fmt(p.from)} → {fmt(p.to)}</div>
        </div>
      ))}
    </div>
  </section>
);

const DeityBlock = ({ x, role, testId }) => (
  <div className="rounded-2xl border-2 border-[#E8A628]/70 p-5" data-testid={testId}>
    <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#B7791F]">{role} lord {x.lord} · D10 deity</div>
    <div className="text-sm text-[#3F2D1B] mt-1 font-semibold" data-testid={`${testId}-period`}>
      {x.mahadasha_lord ? `${x.mahadasha_lord}–${x.lord} Antardasha` : `${x.lord} Mahadasha`} · {fmt(x.from)} → {fmt(x.to)}
    </div>
    <h3 className="font-serif-display text-3xl text-[#3F2D1B] mt-1" data-testid={`${testId}-deity`}>{x.title}</h3>
    <p className="text-xs text-muted mt-1">{x.lord} at {x.rashi} {dms(x.degrees_in_rashi)} · dashamsa {x.dashamsa} of 10 · D10 sign {x.d10_sign}</p>
    <p className="text-sm text-[#3F2D1B]/90 mt-3">Rules {x.rules}.</p>
    <p className="font-serif-display text-2xl text-[#3F2D1B] mt-4" data-testid={`${testId}-mantra`}>{x.mantra}</p>
    <ol className="mt-4 space-y-2">
      {x.steps.map((s, i) => (
        <li key={i} className="flex gap-3 text-[15px] text-[#3F2D1B]"><span className="w-6 h-6 shrink-0 rounded-full bg-[#E8A628] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>{s}</li>
      ))}
    </ol>
  </div>
);

const RemedyCard = ({ r }) => (
  <section className="rounded-2xl bg-white border border-amber-200/70 p-5 sm:p-7 space-y-5" data-testid="d10r-remedy-card">
    <div className="label-eyebrow text-[#B7791F] flex items-center gap-2"><HandsPraying size={14} weight="duotone" /> Your D10 career activation remedy</div>
    <p className="text-[15px] leading-relaxed text-[#3F2D1B]/90" data-testid="d10r-why">{r.why}</p>
    <DeityBlock x={r.antardasha} role="Antardasha" testId="d10r-ad-remedy" />
    {r.same_deity
      ? <p className="text-sm text-muted flex items-center gap-2" data-testid="d10r-same"><Shield size={14} /> Your Mahadasha lord {r.mahadasha.lord} has the same deity, so this one mantra covers both periods.</p>
      : <DeityBlock x={r.mahadasha} role="Mahadasha" testId="d10r-md-remedy" />}
    {r.next_antardasha && (
      <div className="space-y-3" data-testid="d10r-next-section">
        <div className="label-eyebrow text-[#B7791F] flex items-center gap-2"><Bell size={14} weight="duotone" /> Coming next · start before it begins</div>
        <DeityBlock x={r.next_antardasha} role="Next Antardasha" testId="d10r-next-remedy" />
      </div>
    )}
  </section>
);

export default function D10RemedyPage() {
  const [form, setForm] = useState({ date: "", hour: "", minute: "", place: "", timezone: "" });
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const resultRef = useRef(null);

  useBirthDetailsPrefill((bd) => {
    const [hh, mm] = String(bd?.time || "").split(":");
    setForm((f) => ({ date: f.date || bd?.date || "", hour: f.hour || hh || "", minute: f.minute || mm || "",
      place: f.place || bd?.place || "", timezone: f.timezone || bd?.timezone || "" }));
  });

  const birth = { date: form.date, time: `${form.hour}:${form.minute}`, place: form.place, timezone: form.timezone };

  const compute = async (quiet) => {
    setBusy(true);
    try {
      const r = await api.post("/d10-remedy/compute", birth);
      setData(r.data);
      if (!quiet) setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not compute your chart.");
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.date || !form.hour || !form.minute || !form.place) return toast.error("Fill in date, time and place of birth.");
    if (!form.timezone) return toast.error("Pick the birth place from the suggestions list.");
    compute(false);
  };

  useEffect(() => { setData(null); }, [form.date, form.hour, form.minute, form.place]);

  return (
    <PageShell>
      <SEO title="D10 Career Activation Remedy | AstroKuberChat" description="Facing sudden opposition at work? Find the D10 deity of your Mahadasha and Antardasha lords and the mantra to remove career obstacles." />
      <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="d10-remedy-page">
        <div className="label-eyebrow text-muted flex items-center gap-2"><Briefcase size={14} weight="duotone" /> Premium · D10 Dashamsa deities</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight mt-3">D10 Career <span className="text-gold">Activation Remedy</span></h1>
        <p className="text-sm text-muted mt-3 max-w-3xl" data-testid="d10r-intro">Facing sudden opposition or challenges despite a steady job? The answer sits in your D10 career chart. Every planet there is guided by one of ten deities, from Indra and Agni to Varuna and Kubera. Find the deity of your running Mahadasha and Antardasha lords and the mantra that removes your professional obstacles.</p>

        <form onSubmit={submit} className="rounded-2xl bg-white border border-amber-200/70 p-5 sm:p-6 mt-8 grid sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1.4fr_auto] gap-3 items-end" data-testid="d10r-form">
          <label className="text-xs font-semibold text-[#3F2D1B]/70">Date of birth<input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="d10r-date" /></label>
          <div className="text-xs font-semibold text-[#3F2D1B]/70">Time of birth (24-hr)
            <div className="flex gap-2">
              <select value={form.hour} onChange={(e) => setForm({ ...form, hour: e.target.value })} className={inputCls} data-testid="d10r-hour"><option value="">HH</option>{HOURS.map((h) => <option key={h}>{h}</option>)}</select>
              <select value={form.minute} onChange={(e) => setForm({ ...form, minute: e.target.value })} className={inputCls} data-testid="d10r-minute"><option value="">MM</option>{MINUTES.map((m) => <option key={m}>{m}</option>)}</select>
            </div>
          </div>
          <label className="text-xs font-semibold text-[#3F2D1B]/70">Place of birth
            <div className="mt-1"><PlaceAutocompleteInput value={form.place} onChange={(v) => setForm({ ...form, place: v, timezone: "" })} onPick={({ name, timezone }) => setForm((f) => ({ ...f, place: name, timezone }))} placeholder="Start typing your city…" testId="d10r-place" /></div>
          </label>
          <button type="submit" disabled={busy} className="btn-primary rounded-full px-6 py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="d10r-submit">
            <Sparkle size={15} weight="fill" /> {busy ? "Reading…" : "Find my deities"}
          </button>
        </form>

        {data && (
          <div ref={resultRef} className="mt-10 space-y-6 scroll-mt-24" data-testid="d10r-results">
            <PeriodCard d={data} />
            {data.remedy ? <RemedyCard r={data.remedy} /> : (
              <div data-testid="d10r-paywall">
                <PaywallGate
                  tool="d10_remedy"
                  toolLabel="D10 Career Activation Remedy"
                  birthDetails={birth}
                  variant="standalone"
                  onUnlocked={() => { toast.success("Unlocked! Revealing your D10 deities and mantras…"); compute(true); }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </PageShell>
  );
}
